// The customer page's orders table: one row per order, a progress cell per item
// group, and a row that opens to the shipments on it. Hand-typed shipments with
// no order sit in one last row, "Earlier shipments", which also carries the
// manual-entry flow. Sorting keeps that row last in both directions
// (sortUndefined 'last' on every accessor, which it never answers).
import React, { useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { useApi } from '../apiHelpers';
import { DataTable } from '../components/table/DataTable';
import { useToast } from '../components/Toast/ToastContext';
import { buttonClass } from '../components/ui/Button';
import Alert from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import { Field, FieldRow } from '../components/ui/Field';
import { CATEGORY } from '../components/ui/categoryColors';
import Reveal from '../components/ui/Reveal';
import Progress from '../components/ui/Progress';
import Skeleton, { SkeletonGroup } from '../components/ui/Skeleton';
import { openAuthedPdf } from '../Receiving/openAuthedPdf';
import StatusPill from '../Sales/StatusPill';
import ReadingBadge from '../Sales/ReadingBadge';
import { formatEventDate } from '../utils/date-utils';
import { ORDER_TYPE_LABELS } from './types';
import type {
  CustomerOrder,
  CustomerOrderColumn,
  CustomerOrders,
  CustomerOrderShipment,
  EarlierShipment,
} from './types';

interface TableRowData {
  id: string;
  kind: 'order' | 'earlier';
  order?: CustomerOrder;
  earlier?: EarlierShipment[];
}

const EARLIER_ID = 'earlier';

/** The volatile half of the table. Read off `table.options.meta`. */
interface OrdersMeta {
  expanded: Record<string, boolean>;
  onToggle: (id: string) => void;
}

const TYPE_TONE: Record<string, string> = {
  sale: CATEGORY.green.ink.oklch,
  lease: CATEGORY.blue.ink.oklch,
  rental: CATEGORY.orange.ink.oklch,
};

const stop = (e: React.MouseEvent) => e.stopPropagation();

const Chevron: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    className={`h-5 w-5 transition-transform duration-150 ease-emphasized motion-reduce:transition-none ${
      open ? '' : '-rotate-90'
    }`}
  >
    <path d="M7 10l5 5 5-5z" fill="currentColor" />
  </svg>
);

/** Units of an item that count toward the cell: shipped for a sale, out right now for a lease or rental. */
function progressOf(order: CustomerOrder, key: string) {
  const it = order.items[key];
  if (!it) return null;
  const isOut = order.order_type !== 'sale';
  const n = isOut ? Math.max(0, it.shipped - it.returned) : it.shipped;
  return { n, ordered: it.ordered, returned: it.returned, isOut };
}

/** A lease, a rental or an overshoot wears its own colour; the type or the count beside the bar says which. */
function barColor(order: CustomerOrder, n: number, ordered: number): string | undefined {
  if (n > ordered) return CATEGORY.yellow.ink.hex;
  if (order.order_type === 'lease') return CATEGORY.blue.ink.hex;
  if (order.order_type === 'rental') return CATEGORY.orange.ink.hex;
  return undefined;
}

const FIXED_BEFORE: ColumnDef<TableRowData, any>[] = [
  {
    id: 'order',
    header: 'Order',
    accessorFn: (r) => r.order?.so_number,
    sortUndefined: 'last',
    cell: (info) => {
      const { expanded, onToggle } = info.table.options.meta as OrdersMeta;
      const r = info.row.original;
      const open = !!expanded[r.id];
      const name = r.order ? r.order.so_number : 'earlier shipments';
      return (
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            aria-expanded={open}
            aria-label={`${open ? 'Hide' : 'Show'} shipments for ${name}`}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(r.id);
            }}
            className="-m-1.5 rounded p-1.5 text-on-surface-variant hover:text-on-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
          >
            <Chevron open={open} />
          </button>
          {r.order ? (
            <Link
              to={`/orders/${r.order.id}`}
              onClick={stop}
              className="whitespace-nowrap rounded font-mono text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
            >
              {r.order.so_number}
            </Link>
          ) : (
            <span>
              <span className="font-medium text-on-surface">Earlier shipments</span>
              <span className="block text-xs text-on-surface-variant">
                Typed in by hand before orders carried shipments
              </span>
            </span>
          )}
        </div>
      );
    },
  },
  {
    id: 'type',
    header: 'Type',
    accessorFn: (r) => r.order?.order_type,
    sortUndefined: 'last',
    cell: ({ row }) =>
      row.original.order ? (
        <span
          className={`text-xs font-medium ${TYPE_TONE[row.original.order.order_type] ? '' : 'text-on-surface-variant'}`}
          style={TYPE_TONE[row.original.order.order_type] ? { color: TYPE_TONE[row.original.order.order_type] } : undefined}
        >
          {ORDER_TYPE_LABELS[row.original.order.order_type] ?? row.original.order.order_type}
        </span>
      ) : null,
  },
  {
    id: 'status',
    header: 'Status',
    accessorFn: (r) => r.order?.status,
    sortUndefined: 'last',
    cell: ({ row }) => {
      const o = row.original.order;
      if (!o) return null;
      const showReading = o.reading?.state === 'partly' || (o.order_type === 'sale' && (o.reading?.returned ?? 0) > 0);
      return showReading ? <ReadingBadge reading={o.reading} /> : <StatusPill status={o.status} />;
    },
  },
];

const progressColumn = (col: CustomerOrderColumn): ColumnDef<TableRowData, any> => ({
  id: `item_${col.key}`,
  header: col.label,
  sortUndefined: 'last',
  accessorFn: (r) => {
    const p = r.order ? progressOf(r.order, col.key) : null;
    return p && p.ordered > 0 ? p.n / p.ordered : undefined;
  },
  meta: { numeric: true },
  cell: ({ row }) => {
    const o = row.original.order;
    const p = o ? progressOf(o, col.key) : null;
    if (!o || !p) return null;
    return (
      <div className="flex items-center gap-2" title={p.returned > 0 ? `${p.returned} back` : undefined}>
        <Progress
          className="w-16 shrink-0"
          size="md"
          label={`${col.label} ${p.isOut ? 'out' : 'shipped'}, ${o.so_number}`}
          value={p.n}
          max={p.ordered}
          color={barColor(o, p.n, p.ordered)}
          success={o.order_type === 'sale'}
        />
        <span className="whitespace-nowrap text-xs text-on-surface-variant">
          {/* A lease whose drones are all home reads what came back, not "0 out". */}
          {p.isOut && p.n === 0 && p.returned > 0 ? `${p.returned} back` : `${p.n}/${p.ordered}${p.isOut ? ' out' : ''}`}
        </span>
      </div>
    );
  },
});

const LAST_SHIPPED: ColumnDef<TableRowData, any> = {
  id: 'last_shipped',
  header: 'Last shipped',
  accessorFn: (r) => r.order?.last_shipped_on ?? undefined,
  sortUndefined: 'last',
  meta: { align: 'right', numeric: true, cellClass: 'whitespace-nowrap' },
  cell: ({ row }) => {
    const r = row.original;
    if (r.order) {
      return r.order.last_shipped_on ? (
        formatEventDate(r.order.last_shipped_on)
      ) : (
        <span className="text-on-surface-variant">not yet</span>
      );
    }
    const dates = (r.earlier ?? []).map((s) => s.shipped_on).filter(Boolean) as string[];
    return dates.length ? formatEventDate(dates.reduce((a, b) => (a > b ? a : b))) : null;
  },
};

type ShownLine = { label: string; quantity: number; direction?: 'out' | 'return'; drone_serial?: string | null };

/** One shipment's lines folded by item and direction: counts summed, serials gathered. */
function foldLines(lines: ShownLine[]) {
  const groups = new Map<string, { label: string; returned: boolean; qty: number; serials: string[] }>();
  for (const l of lines) {
    const returned = l.direction === 'return';
    const key = `${l.label}|${returned}`;
    const g = groups.get(key) ?? { label: l.label, returned, qty: 0, serials: [] };
    g.qty += l.quantity;
    if (l.drone_serial) g.serials.push(l.drone_serial);
    groups.set(key, g);
  }
  return Array.from(groups.values());
}

const Items: React.FC<{ lines: ShownLine[] }> = ({ lines }) => (
  <ul className="min-w-0 space-y-0.5">
    {foldLines(lines).map((g) => (
      <li key={`${g.label}|${g.returned}`} className="text-sm text-on-surface">
        {g.label} <span className="tabular-nums text-on-surface-variant">x{g.qty}</span>
        {g.returned && <span className="text-xs text-on-surface-variant"> (returned)</span>}
        {g.serials.length > 0 && (
          <div className="break-words font-mono text-xs text-on-surface-variant">{g.serials.join(', ')}</div>
        )}
      </li>
    ))}
  </ul>
);

const Tracking: React.FC<{ value: string | null }> = ({ value }) =>
  value ? (
    <a
      href={`https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(value)}`}
      target="_blank"
      rel="noopener noreferrer"
      className="rounded font-mono text-xs text-blue-400 hover:text-blue-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
    >
      {value}
    </a>
  ) : null;

const SUB_ROW = 'grid gap-x-4 gap-y-1 sm:grid-cols-[8rem_minmax(0,1fr)_auto] items-baseline';

const OrderShipments: React.FC<{ order: CustomerOrder }> = ({ order }) => {
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);

  const openSlip = async (s: CustomerOrderShipment) => {
    setBusy(s.id);
    try {
      // Authed route: a plain link would 401, so the helper fetches it and opens the blob.
      await openAuthedPdf(s.slip_url || `/orders/shipments/${s.id}/slip.pdf`);
    } catch {
      toast.error('The packing slip did not open. Try again.');
    } finally {
      setBusy(null);
    }
  };

  if (order.shipments.length === 0) {
    return (
      <EmptyState variant="line" className="px-4 py-3 pl-12">
        {order.reading?.legacy
          ? 'This order was marked shipped before shipments were recorded here, so it has no serials.'
          : 'Nothing has shipped on this order yet.'}
      </EmptyState>
    );
  }
  return (
    <ul className="space-y-3 px-4 py-3 pl-12">
      {order.shipments.map((s) => (
        <li key={s.id} className={SUB_ROW}>
          <div className="whitespace-nowrap">
            <span className="text-sm font-medium tabular-nums text-on-surface">
              {s.shipped_on ? formatEventDate(s.shipped_on) : 'No date'}
            </span>{' '}
            <span className={`text-xs ${s.direction === 'return' ? 'text-orange-400' : 'text-on-surface-variant'}`}>
              {s.direction === 'return' ? 'Returned' : 'Shipped'}
            </span>
          </div>
          <Items lines={s.lines} />
          <div className="flex items-baseline gap-3">
            <Tracking value={s.tracking_number} />
            <button
              type="button"
              onClick={() => openSlip(s)}
              disabled={busy === s.id}
              className={buttonClass('quiet', 'sm')}
            >
              {busy === s.id ? 'Opening…' : 'Slip'}
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
};

const COUNT_FIELDS = [
  ['drones', 'Drones'],
  ['batteries', 'Batteries'],
  ['chargers', 'Chargers'],
  ['ground_stations', 'Ground stations'],
  ['gear_pelicans', 'Gear pelicans'],
  ['drone_carriers', 'Drone carriers'],
] as const;

const EMPTY_FORM = {
  shipped_on: '',
  drones: 0,
  batteries: 0,
  chargers: 0,
  ground_stations: 0,
  gear_pelicans: 0,
  drone_carriers: 0,
  tracking_number: '',
  notes: '',
};

type ShipForm = typeof EMPTY_FORM;

interface EarlierPanelProps {
  earlier: EarlierShipment[];
  prompt: boolean;
  formOpen: boolean;
  form: ShipForm;
  adding: boolean;
  onForm: (next: ShipForm) => void;
  onAddClick: () => void;
  onManual: () => void;
  onGoToOrders: () => void;
  onSubmit: () => void;
  onCancel: () => void;
  onDelete: (id: string) => void;
}

const EarlierPanel: React.FC<EarlierPanelProps> = (p) => (
  <div className="space-y-3 px-4 py-3 pl-12">
    <div className="flex flex-wrap items-center gap-2">
      <button type="button" onClick={p.onAddClick} className={buttonClass('quiet', 'sm')}>
        {p.formOpen || p.prompt ? 'Cancel' : '+ Add shipment'}
      </button>
    </div>

    <Reveal show={!!p.prompt && !p.formOpen}>
      <Alert
        tone="info"
        live={false}
        action={
          <>
            <button type="button" onClick={p.onGoToOrders} className={buttonClass('quiet', 'sm')}>
              Go to orders
            </button>
            <button type="button" onClick={p.onManual} className={buttonClass('quiet', 'sm')}>
              Manual entry
            </button>
          </>
        }
      >
        This is for a manual shipment entry. You can also add shipping info to an existing order. Would you like to go
        to the orders page?
      </Alert>
    </Reveal>

    <Reveal show={p.formOpen}>
      <div className="space-y-3 rounded-md bg-surface-container-high p-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <FieldRow label="Date" htmlFor="hand-ship-date">
            <Field
              type="date"
              value={p.form.shipped_on}
              onChange={(e) => p.onForm({ ...p.form, shipped_on: e.target.value })}
            />
          </FieldRow>
          {COUNT_FIELDS.map(([key, label]) => (
            <FieldRow key={key} label={label} htmlFor={`hand-ship-${key}`}>
              <Field
                type="number"
                min="0"
                value={p.form[key]}
                onChange={(e) => p.onForm({ ...p.form, [key]: Math.max(0, Number(e.target.value) || 0) })}
              />
            </FieldRow>
          ))}
          <FieldRow label="Tracking #" htmlFor="hand-ship-tracking">
            <Field
              type="text"
              value={p.form.tracking_number}
              onChange={(e) => p.onForm({ ...p.form, tracking_number: e.target.value })}
            />
          </FieldRow>
          <FieldRow label="Notes" htmlFor="hand-ship-notes">
            <Field
              type="text"
              value={p.form.notes}
              onChange={(e) => p.onForm({ ...p.form, notes: e.target.value })}
            />
          </FieldRow>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={p.onSubmit}
            disabled={p.adding}
            className={buttonClass('filled', 'sm')}
          >
            {p.adding ? 'Adding…' : 'Add'}
          </button>
          <button type="button" onClick={p.onCancel} className={buttonClass('quiet', 'sm')}>
            Cancel
          </button>
        </div>
      </div>
    </Reveal>

    {p.earlier.length > 0 && (
      <ul className="space-y-3">
        {p.earlier.map((s) => (
          <li key={s.id} className={SUB_ROW}>
            <span className="whitespace-nowrap text-sm font-medium tabular-nums text-on-surface">
              {s.shipped_on ? formatEventDate(s.shipped_on) : 'No date'}
            </span>
            <div className="min-w-0">
              <Items lines={s.lines} />
              {s.notes && <p className="mt-0.5 text-xs text-on-surface-variant">{s.notes}</p>}
            </div>
            <div className="flex items-baseline gap-3">
              <Tracking value={s.tracking_number} />
              <button
                type="button"
                onClick={() => p.onDelete(s.id)}
                aria-label="Delete shipment"
                className={buttonClass('danger-quiet', 'sm')}
              >
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>
    )}
  </div>
);

interface Props {
  customerId: number;
  data: CustomerOrders | null;
  loading: boolean;
  failed: boolean;
  /** Called after a hand shipment is added or deleted so the page refetches the orders. */
  onChanged: () => void;
}

const CustomerOrdersTable: React.FC<Props> = ({ customerId, data, loading, failed, onChanged }) => {
  const api = useApi();
  const toast = useToast();
  const history = useHistory();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [prompt, setPrompt] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<ShipForm>(EMPTY_FORM);
  const [adding, setAdding] = useState(false);

  const columns = useMemo(
    () => [...FIXED_BEFORE, ...(data?.columns ?? []).map(progressColumn), LAST_SHIPPED],
    [data?.columns],
  );

  const earlier = data?.earlier ?? [];
  const entryOpen = prompt || formOpen;
  const showEarlier = earlier.length > 0 || entryOpen;

  const rows = useMemo<TableRowData[]>(() => {
    const out: TableRowData[] = (data?.orders ?? []).map((o) => ({ id: o.id, kind: 'order', order: o }));
    if (showEarlier) out.push({ id: EARLIER_ID, kind: 'earlier', earlier: data?.earlier ?? [] });
    return out;
  }, [data?.orders, data?.earlier, showEarlier]);

  const onToggle = (id: string) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const startEntry = () => {
    setExpanded((prev) => ({ ...prev, [EARLIER_ID]: true }));
    setPrompt(true);
  };
  const closeEntry = () => {
    setPrompt(false);
    setFormOpen(false);
  };

  const submit = async () => {
    setAdding(true);
    try {
      await api.post(`/customers/${customerId}/shipments`, { body: form });
      setForm(EMPTY_FORM);
      closeEntry();
      toast.success('Shipment added');
      onChanged();
    } catch {
      toast.error('Failed to add shipment');
    } finally {
      setAdding(false);
    }
  };

  const remove = async (shipmentId: string) => {
    if (!window.confirm('Delete this shipment record?')) return;
    try {
      await api.delete(`/customers/shipments/${shipmentId}`);
      toast.success('Shipment deleted');
      onChanged();
    } catch (error: any) {
      toast.error(error?.data?.error?.message || 'Failed to delete shipment');
    }
  };

  const orderCount = data?.orders.length ?? 0;

  return (
    <section aria-label="Orders">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-on-surface-variant">
          <span className="inline-block h-3.5 w-1 rounded-full bg-primary/60" />
          Orders
        </h3>
        {!showEarlier && data && (
          <button type="button" onClick={startEntry} className={buttonClass('tonal', 'sm')}>
            + Add shipment
          </button>
        )}
      </div>
      <DataTable<TableRowData>
        data={rows}
        columns={columns}
        getRowId={(r) => r.id}
        sortable
        meta={{ expanded, onToggle } as OrdersMeta}
        loading={loading}
        loadingContent={
          <SkeletonGroup label="Loading orders" className="space-y-2 px-4 py-4">
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-2/3" />
          </SkeletonGroup>
        }
        empty={
          failed ? (
            <Alert variant="text" tone="error">Orders did not load. Reload the page to try again.</Alert>
          ) : (
            <EmptyState variant="area">No orders yet. Start one with + Sale, + Lease or + Rental above.</EmptyState>
          )
        }
        strip={
          orderCount > 0 ? (
            <span className="text-xs text-on-surface-variant">
              {orderCount} {orderCount === 1 ? 'order' : 'orders'}. Select a row to see its shipments.
            </span>
          ) : undefined
        }
        rowProps={(r) => ({
          onClick: () => onToggle(r.id),
          className: `cursor-pointer ${expanded[r.id] ? 'bg-surface-container' : ''}`,
        })}
        renderSubRow={(r) => {
          if (!expanded[r.id]) return null;
          if (r.order) return <OrderShipments order={r.order} />;
          return (
            <EarlierPanel
              earlier={r.earlier ?? []}
              prompt={prompt}
              formOpen={formOpen}
              form={form}
              adding={adding}
              onForm={setForm}
              onAddClick={() => (entryOpen ? closeEntry() : setPrompt(true))}
              onManual={() => {
                setPrompt(false);
                setFormOpen(true);
              }}
              onGoToOrders={() => history.push(`/orders?customer=${customerId}`)}
              onSubmit={submit}
              onCancel={closeEntry}
              onDelete={remove}
            />
          );
        }}
      />
    </section>
  );
};

export default CustomerOrdersTable;
